<template>
  <Dialog :visible="visible" header="Copy Timetable Entries" :modal="true"
    class="p-fluid w-full max-w-md" @update:visible="emit('close')">
    <p class="text-sm text-gray-600 mb-4">
      Copy all class entries from another {{ classroomLabel }}'s timetable into this one.
      Only {{ classroomLabel }}s using the same bell schedule template are shown.
    </p>

    <div class="field mb-4">
      <label class="font-semibold block mb-1">Copy from</label>
      <Select v-model="sourceId" :options="sourceOptions" optionLabel="label" optionValue="value"
        :placeholder="`Select source ${classroomLabel}`" class="w-full" filter :disabled="sourceOptions.length === 0" />
    </div>

    <div v-if="sourceOptions.length === 0" class="bg-gray-50 p-3 rounded-lg text-sm text-gray-500 text-center">
      No other {{ classroomLabel }}s use the same template yet.
    </div>

    <div v-if="sourceId && sourceTimetable" class="bg-primary/10 p-3 rounded-lg border border-primary/20 text-sm text-primary-700">
      <i class="pi pi-info-circle mr-1"></i>
      {{ sourceEntryCount }} entries will be copied.
      Existing entries in the current timetable will not be overwritten.
    </div>

    <template #footer>
      <Button size="small" label="Cancel" class="p-button-text" @click="emit('close')" />
      <Button size="small" label="Copy Entries" icon="pi pi-copy" class="!bg-primary !border-primary"
        @click="handleCopy" :loading="copying" :disabled="!sourceId || sourceEntryCount === 0" />
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { useGetTimetablesQuery, useUpsertTimetableEntryMutation } from '@/types/vue-apollo-helpers';
import type { TimetableEntry } from '@/types/composition-functions';
import { TeachableType } from '@/types/composition-functions';
import { teachableId as teachableIdFor } from '@/utils/teachable';
import { useSchoolLabels } from '@/composables/useSchoolLabels';
import { useSchoolFeatures } from '@/composables/useSchoolFeatures';
import { getTimeableLabel } from '@/utils/timetableLabel';

const props = defineProps<{
  visible: boolean;
  currentTimetableId: string;
  currentTemplateId: string;
  existingEntries: TimetableEntry[];
}>();

const emit = defineEmits<{
  close: [];
  copied: [];
}>();

const sourceId = ref<string | null>(null);
const copying = ref(false);
const { labels } = useSchoolLabels();
const classroomLabel = computed(() => labels.value.CLASSROOM.toLowerCase());
const { schoolType } = useSchoolFeatures();

const { result: allTimetables } = useGetTimetablesQuery({ first: 200 });
const { mutate: upsertEntry } = useUpsertTimetableEntryMutation();

const compatibleTimetables = computed(() => {
  if (!allTimetables.value?.timetables?.data) return [];
  return allTimetables.value.timetables.data.filter((t) =>
    t.id !== props.currentTimetableId && t.template?.id === props.currentTemplateId,
  );
});

const sourceOptions = computed(() =>
  compatibleTimetables.value.map((t) => ({
    label: getTimeableLabel(t.timeable, {
      title: t.title,
      schoolType: schoolType.value,
      fallback: `Unnamed ${classroomLabel.value}`,
    }),
    value: t.id,
  })),
);

const sourceTimetable = computed(() =>
  compatibleTimetables.value.find((t) => t.id === sourceId.value),
);

const sourceEntryCount = computed(() => sourceTimetable.value?.entries?.length || 0);

const existingKeys = computed(() => {
  const keys = new Set<string>();
  for (const e of props.existingEntries) {
    keys.add(`${e.day_of_week}-${e.timeSlot?.id}`);
  }
  return keys;
});

async function handleCopy() {
  if (!sourceTimetable.value) return;
  copying.value = true;
  try {
    const entries = sourceTimetable.value.entries || [];
    for (const entry of entries) {
      const key = `${entry.day_of_week}-${entry.timeSlot?.id}`;
      if (existingKeys.value.has(key)) continue;

      const teachableId = teachableIdFor(entry);
      await upsertEntry({
        input: {
          timetable_id: props.currentTimetableId,
          timetable_time_slot_id: entry.timeSlot?.id,
          day_of_week: entry.day_of_week,
          teachable_id: teachableId,
          teachable_type: teachableId
            ? (entry.teachable && 'course_title' in entry.teachable ? TeachableType.Course : TeachableType.Subject)
            : null,
          staff_id: entry.staff?.id || null,
          room_name: entry.room_name || '',
          custom_title: entry.custom_title || '',
        },
      });
    }
    emit('copied');
    emit('close');
  } catch {
    // Ignore: copy failed; the finally block resets the copying flag.
  } finally {
    copying.value = false;
  }
}
</script>
